import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AccessorySelector from '../components/AccessorySelector';
import Modal from '../components/Modal';

interface Accessory {
  _id: string;
  name: string;
  quantity: string | number;
  description?: string;
  images?: string;
  code?: string;
  type?: string;
  color?: string;
  unit: string;
}

// Tipler (SizeMeasurement ve TechnicalSpec aynı kalabilir)
interface SizeMeasurement {
  size: string;
  [measurement: string]: number | string;
}

interface TechnicalSpec {
  sizeSpecs?: SizeMeasurement[];
  // Diğer teknik föy alanları...
}

interface Product {
  _id: string;
  name: string;
  productCode?: string;
  technicalSpec?: TechnicalSpec;
}

const SizeSpecificationsPage: React.FC = () => {
  const { isAdmin, hasPermission } = useContext(AuthContext);
  const [productsWithSpecs, setProductsWithSpecs] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [showDebugInfo, setShowDebugInfo] = useState<boolean>(false);
  const [selectedAccessories, setSelectedAccessories] = useState<Accessory[]>([]);
  const [accessoryHistory, setAccessoryHistory] = useState<
    { type: 'ekle' | 'çıkar' | 'güncelle', accessory: Accessory, date: string }[]
  >([]);
  const [debugModal, setDebugModal] = useState<{
    isOpen: boolean;
    title: string;
    data: any;
  }>({
    isOpen: false,
    title: '',
    data: null
  });
  const [debugLogs, setDebugLogs] = useState<
    { message: string; data?: any; date: string }[]
  >([]);

  // Ürünleri yükle
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('http://localhost:8012/api/products?fields=name,productCode,technicalSpec');
        const products: Product[] = response.data;

        const filteredProducts = products.filter(p =>
          p.technicalSpec?.sizeSpecs && Array.isArray(p.technicalSpec.sizeSpecs) && p.technicalSpec.sizeSpecs.length > 0
        );

        setProductsWithSpecs(filteredProducts);
        console.log("Ürünler yüklendi:", filteredProducts);
      } catch (err: any) {
        console.error("Error fetching products:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Ürünler yüklenirken bir hata oluştu.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [isAdmin, hasPermission]);

  // Aksesuar seçildiğinde
  const handleAccessorySelect = (accessory: Accessory) => {
    console.log('%c[onAccessorySelect] ile gelen accessory:', 'color: orange; font-weight: bold;', accessory);
    setSelectedAccessories(prev => {
      const existingIndex = prev.findIndex(a => a._id === accessory._id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = accessory;
        return updated;
      } else {
        return [...prev, accessory];
      }
    });
    setAccessoryHistory(prev => [
      ...prev,
      {
        type: selectedAccessories.some(a => a._id === accessory._id) ? 'güncelle' : 'ekle',
        accessory,
        date: new Date().toISOString()
      }
    ]);
  };

  useEffect(() => {
    console.log('%c[selectedAccessories] güncellendi:', 'color: orange; font-weight: bold;', selectedAccessories);
  }, [selectedAccessories]);

  // Aksesuar kaldırıldığında
  const handleAccessoryRemove = (accessoryId: string) => {
    console.log("Aksesuar kaldırıldı:", accessoryId);
    const removed = selectedAccessories.find(a => a._id === accessoryId);
    setSelectedAccessories(prev => prev.filter(a => a._id !== accessoryId));
    if (removed) {
      setAccessoryHistory(prev => [
        ...prev,
        {
          type: 'çıkar',
          accessory: removed,
          date: new Date().toISOString()
        }
      ]);
    }
  };

  // Debug modalını aç
  const openDebugModal = (title: string, data: any) => {
    setDebugModal({
      isOpen: true,
      title,
      data
    });
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      {/* Başlık ve Debug Butonu */}
      <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-200">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Ölçü Tablosu Olan Ürünler
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => openDebugModal('Seçili Aksesuarlar', selectedAccessories)}
            className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1 rounded"
          >
            Aksesuar Debug
          </button>
          <button
            onClick={() => openDebugModal('Ürünler', productsWithSpecs)}
            className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded"
          >
            Ürün Debug
          </button>
        </div>
      </div>

      {/* Aksesuar Seçici */}
      <div className="mb-6">
        <AccessorySelector
          selectedAccessories={selectedAccessories}
          onAccessorySelect={handleAccessorySelect}
          onAccessoryRemove={handleAccessoryRemove}
        />
      </div>

      {/* Yükleniyor durumu */}
      {loading && (
        <div className="flex justify-center items-center p-10">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mr-4 border-t-purple-600 animate-spin"></div>
          <span className="text-gray-500">Ölçü tabloları yükleniyor...</span>
        </div>
      )}

      {/* Hata durumu */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-sm animate-fade-in" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/* Veri yok mesajı */}
      {!loading && !error && productsWithSpecs.length === 0 && (
        <div className="text-center p-6 text-gray-500">
          <p>Ölçü tablosu içeren ürün bulunamadı.</p>
        </div>
      )}

      {/* Ürün Listesi */}
      {!loading && !error && productsWithSpecs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {productsWithSpecs.map((product) => (
            <Link
              key={product._id}
              to={`/size-spec-detail/${product._id}`}
              className="block bg-gray-50 p-4 rounded-lg shadow-md border border-gray-200 hover:shadow-lg hover:border-blue-300 transition duration-200"
            >
              <h2 className="text-lg font-semibold text-gray-800 truncate mb-1">
                {product.name}
              </h2>
              <p className="text-sm text-gray-600">
                {product.productCode ? `(${product.productCode})` : '(Kod Yok)'}
              </p>
              <span className="mt-2 inline-block bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded border border-purple-300">
                Ölçü Tablosu Detayı
              </span>
            </Link>
          ))}
        </div>
      )}

      {/* Debug Modal */}
      <Modal
        isOpen={debugModal.isOpen}
        onClose={() => setDebugModal(prev => ({ ...prev, isOpen: false }))}
        title={debugModal.title}
        type="info"
      >
        <div className="bg-gray-800 text-gray-100 rounded p-4">
          <pre className="text-xs overflow-auto max-h-[60vh]">
            {JSON.stringify(debugModal.data, null, 2)}
          </pre>
        </div>
      </Modal>

       {/* Stil Tanımları */}
       <style jsx>{`
        .loader {
          /* border-top-color: #9333EA; */ /* purple-600 - already set inline */
          animation: spinner 1.5s linear infinite;
        }
        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SizeSpecificationsPage;
